import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { QuestionDetail } from '@shared-ui/questions-page';
import { questionApi, type QuestionCurriculumMembership } from '../../api/questionApi';

/** One curriculum row: name + where the question's module sits in its book ToC. */
function CurriculumRow({ m }: { m: QuestionCurriculumMembership }) {
  const gradeLabel = m.examTrack ? m.board : `${m.board} · Class ${m.grade ?? '?'}`;
  return (
    <div
      style={{
        border: '1px solid var(--border-color)', background: 'var(--surface)',
        borderRadius: 6, padding: '8px 10px', marginBottom: 8,
        opacity: m.isActive ? 1 : 0.72,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ color: 'var(--text-primary)', fontSize: 14 }}>{m.name}</strong>
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{gradeLabel}</span>
        {!m.isActive && (
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-tertiary)', borderRadius: 4, padding: '1px 6px' }}>
            inactive
          </span>
        )}
        {m.ownerType === 'teacher' && (
          <span style={{ fontSize: 11, fontWeight: 600, color: '#2563eb', background: 'var(--bg-tertiary)', borderRadius: 4, padding: '1px 6px' }}>
            teacher
          </span>
        )}
        {!m.gradeMatch && (
          <span
            title="This question's Serves (board × grade) does not cover this curriculum's grade — its module is placed here, but students reading this grade will not be served it."
            style={{ fontSize: 11, fontWeight: 600, color: '#b45309', background: '#fef3c7', borderRadius: 4, padding: '1px 6px' }}
          >
            grade mismatch
          </span>
        )}
        {!m.examMatch && (
          <span
            title="This question's Serves (board × grade) does not cover this curriculum's board — its module is placed here, but students on this board will not be served it."
            style={{ fontSize: 11, fontWeight: 600, color: '#b45309', background: '#fef3c7', borderRadius: 4, padding: '1px 6px' }}
          >
            exam mismatch
          </span>
        )}
      </div>
      <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
        {m.nodes.map(n => (
          <span key={n.placementId} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 10 }}>
            <span style={{ color: 'var(--text-tertiary)' }}>{n.bookLevel} {n.number}</span>
            <span>{n.displayName}</span>
            {n.isPrimary && (
              <span title="Primary mapping (this book node's canonical identity)" style={{ color: 'var(--color-primary)', fontWeight: 700 }}>★</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Shared presentational panel — the "Curricula" heading, loading/empty/error
 *  states, and the curriculum rows. Used by both the read-only detail view and
 *  the live edit preview. */
function CurriculaPanel({
  moduleId, moduleName, curricula, isLoading, error, collapsible,
}: {
  moduleId: string | null | undefined;
  moduleName?: string;
  curricula: QuestionCurriculumMembership[];
  isLoading: boolean;
  error: unknown;
  /** When true, the "Curricula" heading toggles the panel open/closed (like the
   *  Concepts subsection). Starts expanded. Read-only detail view omits it. */
  collapsible?: boolean;
}) {
  const [open, setOpen] = useState(true);
  const showBody = !collapsible || open;

  const count = curricula.length > 0 && (
    <span style={{ marginLeft: 8, fontSize: 13, color: 'var(--text-tertiary)', fontWeight: 400 }}>
      ({curricula.length})
    </span>
  );

  return (
    <div className="view-section">
      {collapsible ? (
        <h3
          role="button"
          tabIndex={0}
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
        >
          <span style={{ display: 'inline-block', width: 12, transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'none' }}>▸</span>
          Curricula
          {count}
        </h3>
      ) : (
        <h3>Curricula{count}</h3>
      )}

      {showBody && (
        <>
          {isLoading && (
            <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: '4px 0 8px' }}>Loading…</p>
          )}

          {!!error && (
            <p style={{ color: 'var(--color-error)', fontSize: 13, margin: '4px 0 8px' }}>
              {error instanceof Error ? error.message : 'Failed to load curricula'}
            </p>
          )}

          {!isLoading && !error && moduleId == null && (
            <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: '4px 0 8px' }}>
              This question has no module — it can't belong to any curriculum until a module is assigned.
            </p>
          )}

          {!isLoading && !error && moduleId != null && curricula.length === 0 && (
            <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: '4px 0 8px' }}>
              Not part of any curriculum yet. Its module{moduleName ? ` (${moduleName})` : ''} isn't mapped into any curriculum's table of contents.
            </p>
          )}

          {curricula.map(m => (
            <CurriculumRow key={m.curriculumId} m={m} />
          ))}
        </>
      )}
    </div>
  );
}

/** Read-only panel showing which curricula this (saved) question is part of,
 *  resolved from its canonical module. Admin-console only; mounted via the shared
 *  question detail view's `renderDetailExtra` slot. See getQuestionCurricula.ts. */
export function QuestionCurricula({ question }: { question: QuestionDetail }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['question-curricula', question.id],
    queryFn: () => questionApi.getQuestionCurricula(question.id),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <CurriculaPanel
      moduleId={data?.module?.id}
      moduleName={data?.module?.name}
      curricula={data?.curricula ?? []}
      isLoading={isLoading}
      error={error}
    />
  );
}

/** Live edit-form preview: resolves membership from the UNSAVED module / class /
 *  target-exam values and re-queries whenever they change. See getCurriculaPreview.ts. */
export function QuestionCurriculaPreview({
  moduleId, classes, targetExams,
}: {
  moduleId?: string | null;
  classes?: string[];
  targetExams?: string[];
}) {
  const normClasses = classes ?? [];
  const normExams = targetExams ?? [];
  const { data, isLoading, error } = useQuery({
    // Key on the exact inputs so a module/class/exam change refetches (annotate mode).
    queryKey: ['curricula-preview', moduleId ?? null, [...normClasses].sort(), [...normExams].sort()],
    queryFn: () => questionApi.getCurriculaPreview({ moduleId, classes: normClasses, targetExams: normExams }),
    enabled: !!moduleId,
    staleTime: 60 * 1000,
  });

  // No module chosen yet — show the "assign a module" hint rather than firing a query.
  if (!moduleId) {
    return <CurriculaPanel moduleId={null} curricula={[]} isLoading={false} error={null} collapsible />;
  }

  return (
    <CurriculaPanel
      moduleId={moduleId}
      curricula={data?.curricula ?? []}
      isLoading={isLoading}
      error={error}
      collapsible
    />
  );
}
